import React from 'react';
import DevicesOutlined from '@mui/icons-material/DevicesOutlined';
import FingerprintOutlined from '@mui/icons-material/FingerprintOutlined';
import GppGoodOutlined from '@mui/icons-material/GppGoodOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import MailOutlined from '@mui/icons-material/MailOutlined';
import SmartphoneOutlined from '@mui/icons-material/SmartphoneOutlined';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';

import { D4_TOKENS, D4Skin, typeStyle } from './d4Tokens';

/**
 * Shared primitives for the registration prototype — every value and SVG path
 * here is lifted from the Claude Design overlays. The design file wins.
 */

// Fonts: functional families only (owner refinement 2026-08-02 — Rye,
// Special Elite, and Playfair dropped from the overlay system). Same Google
// Fonts posture as the harness's preview-head.html; browsers dedupe @imports.
//
// Interaction states live here as real CSS (:hover/:active/:focus-visible)
// rather than JS state — the design file expressed them as `style-hover`
// attributes, and Luma's polish is largely in these transitions. Per-skin
// values arrive as CSS custom properties set on the card root.
export const CARD_KEYFRAMES = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Figtree:wght@400;600;700;800&display=swap');
@keyframes ovStepIn{from{opacity:0;transform:translateX(14px);}to{opacity:1;transform:translateX(0);}}
@keyframes ovTick{0%{transform:scale(.5);opacity:0;}60%{transform:scale(1.08);}100%{transform:scale(1);opacity:1;}}
@keyframes d4Spark{0%{transform:translate(0,0) scale(1);opacity:1;}70%{opacity:.9;}100%{transform:translate(var(--dx),var(--dy)) scale(.25);opacity:0;}}
@keyframes d4Ring{0%{transform:scale(.2);opacity:.85;}100%{transform:scale(2.1);opacity:0;}}
@keyframes d4Pop{0%{transform:scale(.4);opacity:0;}55%{transform:scale(1.12);opacity:1;}100%{transform:scale(1);opacity:1;}}
@media (prefers-reduced-motion: reduce){
  .d4-spark,.d4-ring{animation:none !important;opacity:0 !important;}
}

.d4-primary{transition:filter .15s ease,transform .12s ease,opacity .15s ease;}
.d4-primary:hover:not(:disabled){filter:brightness(1.07);}
.d4-primary:active:not(:disabled){transform:translateY(1px);}

.d4-method{transition:background .15s ease,border-color .15s ease,transform .12s ease;}
.d4-method:hover{background:var(--d4-method-hover-bg) !important;border-color:var(--d4-method-hover-border) !important;}
.d4-method:active{transform:translateY(1px);}

.d4-pill{transition:background .15s ease,transform .12s ease;}
.d4-pill:hover{background:var(--d4-pill-hover) !important;}
.d4-pill:active{transform:translateY(1px);}

.d4-link{transition:color .15s ease;}
.d4-link:hover{color:var(--d4-link-hover) !important;}

.d4-notnow{transition:color .15s ease,background .15s ease;border-radius:8px;}
.d4-notnow:hover{color:var(--d4-text) !important;background:var(--d4-pill-bg);}

.d4-chrome span{transition:border-color .15s ease,color .15s ease,background .15s ease;}
.d4-chrome:hover span{border-color:var(--d4-chrome-hover-border) !important;color:var(--d4-text) !important;}

.d4-checkrow{transition:border-color .15s ease,background .15s ease;}
.d4-checkrow:hover{border-color:var(--d4-method-hover-border) !important;}

.d4-input{transition:border-color .15s ease,box-shadow .15s ease;}
.d4-input:hover:not(:focus){border-color:var(--d4-method-hover-border);}

/* Keyboard focus is always visible (WCAG 2.4.7 / AAA posture). */
.d4-focusable:focus-visible{outline:2px solid var(--d4-focus);outline-offset:2px;}
.d4-input:focus,.d4-code:focus{border-color:var(--d4-focus) !important;box-shadow:var(--d4-focus-ring) !important;}

@media (prefers-reduced-motion: reduce){
  .d4-primary,.d4-method,.d4-pill,.d4-link,.d4-notnow,.d4-chrome span,.d4-checkrow,.d4-input{transition:none;}
}
`;

export type DiscIcon = 'door' | 'mail' | 'phone' | 'shield' | 'pin' | 'passkey';

/**
 * ICONOGRAPHY STANDARD (see `factories/product/icon-standard.md`).
 *
 * Utility icons come from the app's existing pack — `@mui/icons-material`,
 * already a dependency with ~85 usages across the game client — in a single
 * variant family (`Outlined`) so optical weight, grid, and terminals are
 * consistent by construction rather than by a drafter's eye. The step icons
 * below replaced hand-drawn paths on 2026-08-02 (owner review: several of the
 * hand-drawn ones did not read cleanly at 19px).
 *
 * Custom SVG is reserved for (a) vendor marks, which must be the vendor's own
 * artwork (see GoogleGlyph / AppleGlyph / PasskeyGlyph), and (b) poker-domain
 * metaphors no pack carries — the house precedent for those is
 * `TableHeader/WesternIcons.tsx` and `SaloonLobby/icons.tsx`.
 */
const DISC_ICONS: Record<DiscIcon, React.ComponentType<{ sx?: object }>> = {
  door: LoginOutlined, // entering the account — the design's own metaphor
  mail: MailOutlined,
  phone: SmartphoneOutlined,
  shield: GppGoodOutlined, // eligibility / house rule
  pin: LocationOnOutlined,
  passkey: FingerprintOutlined
};

/** The 42px step icon disc that opens every screen. */
export function IconDisc({ skin, icon }: { skin: D4Skin; icon: DiscIcon }) {
  const t = D4_TOKENS[skin];
  const Icon = DISC_ICONS[icon];
  return (
    <div
      aria-hidden='true'
      style={{
        width: 42,
        height: 42,
        borderRadius: '50%',
        background: t.disc.bg,
        border: t.disc.border,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
      <Icon sx={{ fontSize: 21, color: t.disc.stroke }} />
    </div>
  );
}

export function Heading({
  skin,
  center,
  children
}: {
  skin: D4Skin;
  center?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{ ...typeStyle(D4_TOKENS[skin].heading), textAlign: center ? 'center' : undefined }}>
      {children}
    </div>
  );
}

export function BodyText({
  skin,
  center,
  children
}: {
  skin: D4Skin;
  center?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        ...typeStyle(D4_TOKENS[skin].body),
        marginTop: 5,
        textAlign: center ? 'center' : undefined
      }}>
      {children}
    </div>
  );
}

/** Field-label row with the optional inline toggle link on the right (design pattern).
 *  `error` turns the label text the skin's error color, matching LinkContactCard's
 *  label+border+message treatment (owner 2026-08-03 — error states must be consistent). */
export function LabelRow({
  skin,
  label,
  action,
  onAction,
  error
}: {
  skin: D4Skin;
  label: string;
  action?: string;
  onAction?: () => void;
  error?: boolean;
}) {
  const t = D4_TOKENS[skin];
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ ...typeStyle(t.label), color: error ? t.error : t.label.color }}>{label}</span>
      {action ? (
        <button
          type='button'
          onClick={onAction}
          className='d4-link d4-focusable'
          style={{
            cursor: 'pointer',
            background: 'transparent',
            border: 0,
            ...hitArea(),
            fontSize: 11.5,
            fontWeight: 600,
            color: t.toggleColor,
            fontFamily: t.body.fontFamily
          }}>
          {action}
        </button>
      ) : null}
    </div>
  );
}

export function textInputStyle(
  skin: D4Skin,
  focused?: boolean,
  error?: boolean
): React.CSSProperties {
  const t = D4_TOKENS[skin];
  return {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    borderRadius: 8,
    background: t.input.bg,
    border: error
      ? `1px solid ${t.error}`
      : focused
      ? `1px solid ${t.input.focusBorder}`
      : t.input.border,
    boxShadow: focused ? t.input.focusRing : undefined,
    color: t.input.color,
    fontFamily: t.input.fontFamily,
    fontSize: 14,
    outline: 'none',
    transition: 'all .15s ease'
  };
}

export function PrimaryButton({
  skin,
  disabled,
  onClick,
  children
}: {
  skin: D4Skin;
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const t = D4_TOKENS[skin];
  return (
    <button
      type='button'
      disabled={disabled}
      onClick={onClick}
      className='d4-primary d4-focusable'
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: '100%',
        border: 0,
        borderRadius: 8,
        padding: 12,
        // AAA target floor — TCH's 13px label left the button at 40px tall
        // (caught by the 2026-08-02 audit sweep); Hijack's 14px cleared it.
        minHeight: 44,
        ...typeStyle(t.primaryBtn.font),
        background: t.primaryBtn.background,
        boxShadow: t.primaryBtn.shadow,
        transition: 'all .15s ease',
        opacity: disabled ? 0.45 : 1
      }}>
      {children}
    </button>
  );
}

export function MethodButton({
  skin,
  onClick,
  icon,
  children
}: {
  skin: D4Skin;
  onClick?: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const t = D4_TOKENS[skin];
  return (
    <button
      type='button'
      onClick={onClick}
      className='d4-method d4-focusable'
      style={{
        cursor: 'pointer',
        width: '100%',
        border: t.methodBtn.border,
        borderRadius: 8,
        padding: 12,
        minHeight: 44,
        ...typeStyle(t.methodBtn.font),
        background: t.methodBtn.background,
        boxShadow: skin === 'hijack' ? 'inset 0 1px 0 rgba(255,180,92,.12)' : undefined,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 9,
        transition: 'all .15s ease'
      }}>
      {icon}
      {children}
    </button>
  );
}

export function NotNowButton({
  skin,
  onClick,
  children = 'Not now'
}: {
  skin: D4Skin;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  const t = D4_TOKENS[skin];
  return (
    <button
      type='button'
      onClick={onClick}
      className='d4-notnow d4-focusable'
      style={{
        cursor: 'pointer',
        width: '100%',
        background: 'transparent',
        border: 0,
        padding: 13,
        minHeight: 44,
        ...typeStyle(t.notNow)
      }}>
      {children}
    </button>
  );
}

/**
 * Luma-style "Paste Code" pill — a filled affordance with a clipboard glyph,
 * not a bare text link (owner reference 2026-08-02). Hover/active handled by
 * the .d4-pill rules.
 */
export function PasteCodeButton({ skin, onClick }: { skin: D4Skin; onClick?: () => void }) {
  const t = D4_TOKENS[skin];
  return (
    <button
      type='button'
      onClick={onClick}
      className='d4-pill d4-focusable'
      style={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 44,
        padding: '10px 16px',
        borderRadius: 999,
        border: 0,
        background: t.pillBg,
        color: t.heading.color,
        fontFamily: t.body.fontFamily,
        fontSize: 13,
        fontWeight: 600
      }}>
      <svg
        width='15'
        height='15'
        viewBox='0 0 24 24'
        fill='none'
        aria-hidden='true'
        style={{ flex: 'none' }}>
        <rect x='8' y='4' width='12' height='16' rx='2.5' stroke='currentColor' strokeWidth='1.7' />
        <path
          d='M16 4V3a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v1'
          stroke='currentColor'
          strokeWidth='1.7'
          strokeLinecap='round'
        />
        <path
          d='M16 20v.5a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 20.5v-11A1.5 1.5 0 0 1 5.5 8H8'
          stroke='currentColor'
          strokeWidth='1.7'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
      Paste Code
    </button>
  );
}

export type BulletIcon = 'key' | 'lock' | 'devices';

const BULLET_ICONS: Record<BulletIcon, React.ComponentType<{ sx?: object }>> = {
  key: VpnKeyOutlined,
  lock: LockOutlined,
  devices: DevicesOutlined
};

/**
 * Benefit bullets — icon + line, used to make a value proposition scannable
 * instead of a paragraph (owner reference 2026-08-02, Luma passkey screen).
 * Icons follow the iconography standard: pack, Outlined family.
 */
export function BulletList({
  skin,
  items
}: {
  skin: D4Skin;
  items: { icon: BulletIcon; text: string }[];
}) {
  const t = D4_TOKENS[skin];
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
      }}>
      {items.map((item) => {
        const Icon = BULLET_ICONS[item.icon];
        return (
          <li key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Icon sx={{ fontSize: 19, color: t.body.color, flex: 'none' }} />
            <span
              style={{
                fontSize: 13.5,
                lineHeight: 1.45,
                color: t.heading.color,
                fontFamily: t.body.fontFamily
              }}>
              {item.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Two firework bursts behind the finale (owner 2026-08-03). The mid-flow
 * eligibility tick is a quiet "you're cleared"; this is the moment the player
 * is actually in, so it should feel bigger. Purely decorative and
 * `aria-hidden`, and it collapses entirely under `prefers-reduced-motion` —
 * the copy alone still carries the message.
 */
export function Fireworks({ skin }: { skin: D4Skin }) {
  const t = D4_TOKENS[skin];
  const bursts = [
    { x: '18%', y: '18%', delay: 0, color: t.avatar.ring },
    { x: '80%', y: '30%', delay: 380, color: t.tick.stroke }
  ];
  const SPARKS = 10;
  return (
    <div
      aria-hidden='true'
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {bursts.map((b) => (
        <div
          key={`${b.x}-${b.y}`}
          style={{ position: 'absolute', left: b.x, top: b.y, width: 0, height: 0 }}>
          <span
            className='d4-ring'
            style={{
              position: 'absolute',
              left: -22,
              top: -22,
              width: 44,
              height: 44,
              borderRadius: '50%',
              border: `2px solid ${b.color}`,
              animation: `d4Ring .9s cubic-bezier(.2,.8,.2,1) ${b.delay}ms both`
            }}
          />
          {Array.from({ length: SPARKS }, (_, i) => {
            const angle = (i / SPARKS) * Math.PI * 2;
            const dist = 44 + (i % 3) * 9;
            return (
              <span
                key={i}
                className='d4-spark'
                style={
                  {
                    position: 'absolute',
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background: b.color,
                    '--dx': `${Math.cos(angle) * dist}px`,
                    '--dy': `${Math.sin(angle) * dist}px`,
                    animation: `d4Spark 1.1s cubic-bezier(.15,.7,.3,1) ${b.delay + i * 18}ms both`
                  } as React.CSSProperties
                }
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function OrDivider({ skin }: { skin: D4Skin }) {
  const t = D4_TOKENS[skin];
  const line = t.rule;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} aria-hidden='true'>
      <span style={{ flex: 1, height: 1, background: line }} />
      <span style={{ ...typeStyle(t.micro), fontSize: 9, letterSpacing: '.2em' }}>OR</span>
      <span style={{ flex: 1, height: 1, background: line }} />
    </div>
  );
}

export function FooterMicro({ skin }: { skin: D4Skin }) {
  return (
    <div style={{ textAlign: 'center', ...typeStyle(D4_TOKENS[skin].micro) }}>
      TCH One · one account, every table ♠
    </div>
  );
}

function methodIconColor(skin: D4Skin): string {
  return skin === 'hijack' ? '#f1e3c7' : '#000000';
}

/**
 * Google "G" — OFFICIAL ARTWORK: the four path segments are verbatim from
 * Google's own asset (gstatic CDN, "Copyright (c) 2016 Google Inc.", archived
 * at ./assets/google-g-official.svg, downloaded 2026-08-02). The earlier
 * version in this file was a widely-circulated approximation carrying Material
 * palette colors (#FFC107/#FF3D00/#4CAF50/#1976D2) rather than Google's brand
 * colors — replaced.
 *
 * Treatment: monochrome by default (owner decision 2026-08-02 — Luma-pattern
 * design-system cohesion). Google's branding spec asks for the multi-color G;
 * this is a documented, owner-accepted deviation (PRD AC40) — enforcement
 * happens at OAuth verification around consent/scopes, not button art. Pass
 * `official` to render Google's brand colors instead: that is the entire
 * swap-back, should Legal or Google ever ask for it.
 */
const GOOGLE_G_PATHS: { d: string; brand: string }[] = [
  {
    brand: '#4285F4',
    d: 'M117.6,61.3636364 C117.6,57.1090909 117.218182,53.0181818 116.509091,49.0909091 L60,49.0909091 L60,72.3 L92.2909091,72.3 C90.9,79.8 86.6727273,86.1545455 80.3181818,90.4090909 L80.3181818,105.463636 L99.7090909,105.463636 C111.054545,95.0181818 117.6,79.6363636 117.6,61.3636364 L117.6,61.3636364 Z'
  },
  {
    brand: '#34A853',
    d: 'M60,120 C76.2,120 89.7818182,114.627273 99.7090909,105.463636 L80.3181818,90.4090909 C74.9454545,94.0090909 68.0727273,96.1363636 60,96.1363636 C44.3727273,96.1363636 31.1454545,85.5818182 26.4272727,71.4 L6.38181818,71.4 L6.38181818,86.9454545 C16.2545455,106.554545 36.5454545,120 60,120 L60,120 Z'
  },
  {
    brand: '#FBBC05',
    d: 'M26.4272727,71.4 C25.2272727,67.8 24.5454545,63.9545455 24.5454545,60 C24.5454545,56.0454545 25.2272727,52.2 26.4272727,48.6 L26.4272727,33.0545455 L6.38181818,33.0545455 C2.31818182,41.1545455 0,50.3181818 0,60 C0,69.6818182 2.31818182,78.8454545 6.38181818,86.9454545 L26.4272727,71.4 L26.4272727,71.4 Z'
  },
  {
    brand: '#EA4335',
    d: 'M60,23.8636364 C68.8090909,23.8636364 76.7181818,26.8909091 82.9363636,32.8363636 L100.145455,15.6272727 C89.7545455,5.94545455 76.1727273,0 60,0 C36.5454545,0 16.2545455,13.4454545 6.38181818,33.0545455 L26.4272727,48.6 C31.1454545,34.4181818 44.3727273,23.8636364 60,23.8636364 L60,23.8636364 Z'
  }
];

export function GoogleGlyph({ skin, official }: { skin: D4Skin; official?: boolean }) {
  const mono = methodIconColor(skin);
  return (
    <svg width='19' height='19' viewBox='0 0 118 120' aria-hidden='true' style={{ flex: 'none' }}>
      {GOOGLE_G_PATHS.map((p) => (
        <path key={p.brand} fill={official ? p.brand : mono} d={p.d} />
      ))}
    </svg>
  );
}

/**
 * Apple mark — OFFICIAL ARTWORK: path extracted verbatim from Apple's
 * "Sign in with Apple — Logo Only" SVG (Logo-Sign-in-with-Apple.dmg from
 * developer.apple.com design resources, downloaded 2026-08-02; source SVGs +
 * Apple Design Resources License archived in ./assets/). Monochrome white on
 * dark / black on light is the HIG-approved treatment; viewBox is a tight
 * crop of the official 56×56 frame so the mark sizes to the label cap height.
 */
export function AppleGlyph({ skin }: { skin: D4Skin }) {
  return (
    <svg
      width='17'
      height='20'
      viewBox='19 14.5 18 21.5'
      fill={skin === 'hijack' ? '#f1e3c7' : '#000000'}
      aria-hidden='true'
      style={{ transform: 'translateY(-1px)', flex: 'none' }}>
      <path
        fillRule='nonzero'
        d='M28.2226562,20.3846154 C29.0546875,20.3846154 30.0976562,19.8048315 30.71875,19.0317864 C31.28125,18.3312142 31.6914062,17.352829 31.6914062,16.3744437 C31.6914062,16.2415766 31.6796875,16.1087095 31.65625,16 C30.7304687,16.0362365 29.6171875,16.640178 28.9492187,17.4494596 C28.421875,18.06548 27.9414062,19.0317864 27.9414062,20.0222505 C27.9414062,20.1671964 27.9648438,20.3121424 27.9765625,20.3604577 C28.0351562,20.3725366 28.1289062,20.3846154 28.2226562,20.3846154 Z M25.2929688,35 C26.4296875,35 26.9335938,34.214876 28.3515625,34.214876 C29.7929688,34.214876 30.109375,34.9758423 31.375,34.9758423 C32.6171875,34.9758423 33.4492188,33.792117 34.234375,32.6325493 C35.1132812,31.3038779 35.4765625,29.9993643 35.5,29.9389701 C35.4179688,29.9148125 33.0390625,28.9122695 33.0390625,26.0979021 C33.0390625,23.6579784 34.9140625,22.5588048 35.0195312,22.474253 C33.7773438,20.6382708 31.890625,20.5899555 31.375,20.5899555 C29.9804688,20.5899555 28.84375,21.4596313 28.1289062,21.4596313 C27.3554688,21.4596313 26.3359375,20.6382708 25.1289062,20.6382708 C22.8320312,20.6382708 20.5,22.5950413 20.5,26.2911634 C20.5,28.5861411 21.3671875,31.013986 22.4335938,32.5842339 C23.3476562,33.9129053 24.1445312,35 25.2929688,35 Z'
      />
    </svg>
  );
}

/**
 * Passkey — OFFICIAL ARTWORK: the FIDO Alliance passkey mark. Paths are
 * verbatim from FIDO's own SVG (fidoalliance.org "get the passkey icon",
 * archived at ./assets/fido-passkey-mark-official.svg, downloaded 2026-08-02):
 * the shoulders, head, and notched key that make up the industry-standard
 * passkey symbol. FIDO's file wraps these in a white rounded-square container;
 * for an in-button glyph we drop the container and render the mark itself,
 * monochrome, cropped to a tight square viewBox. FIDO encourages adoption of
 * the mark — no restrictive brand rules apply.
 */
export function PasskeyGlyph({ skin }: { skin: D4Skin }) {
  const c = methodIconColor(skin);
  return (
    <svg width='20' height='20' viewBox='47 55 116 116' aria-hidden='true' style={{ flex: 'none' }}>
      {/* key, with its bit-hole punched by the even-odd rule */}
      <path
        fill={c}
        fillRule='evenodd'
        clipRule='evenodd'
        d='M155.56,102.99c0,9.8-6.03,18.13-14.42,21.17l5.08,8.41l-7.51,9.24l7.51,9.03l-12.12,16.26l-8.54-9.11v-18.41v-16.04c-7.59-3.45-12.91-11.35-12.91-20.55c0-12.37,9.61-22.4,21.45-22.4C145.95,80.6,155.56,90.62,155.56,102.99z M134.1,106.43c2.86,0,5.18-2.42,5.18-5.41c0-2.99-2.32-5.41-5.18-5.41s-5.18,2.42-5.18,5.41C128.92,104.01,131.24,106.43,134.1,106.43z'
      />
      {/* shoulders */}
      <path
        fill={c}
        fillRule='evenodd'
        clipRule='evenodd'
        d='M118.53,127.62c-6.93-5.69-11.58-14.43-12.22-24.36H69.17c-7.79,0-14.1,6.41-14.1,14.31v17.89c0,3.95,3.16,7.16,7.05,7.16h49.36c3.89,0,7.05-3.2,7.05-7.16V127.62z'
      />
      {/* head */}
      <path
        fill={c}
        d='M85.07,98.97c-1.72-0.33-3.43-0.64-5.05-1.32c-6.15-2.58-9.73-7.34-10.89-14.06c-0.8-4.6-0.42-9.15,1.44-13.45c2.64-6.11,7.39-9.43,13.61-10.55c3.72-0.67,7.43-0.52,11.02,0.82c5.4,2.01,9.01,5.87,10.69,11.55c1.69,5.72,1.44,11.45-1.11,16.86c-2.64,5.66-7.26,8.69-13.1,9.88c-0.49,0.1-0.97,0.2-1.46,0.29C88.51,98.97,86.79,98.97,85.07,98.97z'
      />
    </svg>
  );
}

/**
 * Presentation variant, from the design file's `variant` prop: 'modal'
 * (centered card) or 'sheet' (mobile bottom drawer — full width, 22px top
 * radius, grab handle, deeper bottom padding). Context so every card in a
 * flow inherits it without prop-drilling.
 */
export const CardVariantContext = React.createContext<'modal' | 'sheet'>('modal');

/** ≥44×44 hit area for small text links (WCAG 2.5.5 AAA) without moving layout. */
export function hitArea(): React.CSSProperties {
  return { padding: '15px 12px', margin: '-15px -12px' };
}

export interface CardShellProps {
  skin: D4Skin;
  children: React.ReactNode;
  onClose?: () => void;
  onBack?: () => void;
  /** ovStepIn on mount (per-step, as designed) */
  animate?: boolean;
  /**
   * Terminal card: no close, no back. The done view auto-dismisses, so a
   * close affordance would offer an exit that leads nowhere different
   * (owner 2026-08-03).
   */
  hideChrome?: boolean;
}

/**
 * The overlay card: #-exact chrome from the design — 500px max width, radius
 * 16, the design's border/shadow, close (top-right) and back (top-left)
 * circles, body padding 40px 20px 9px. Height animation omitted in the
 * prototype (the design animates container height between steps).
 */
export function CardShell({
  skin,
  children,
  onClose,
  onBack,
  animate = true,
  hideChrome
}: CardShellProps) {
  const t = D4_TOKENS[skin];
  const variant = React.useContext(CardVariantContext);
  const sheet = variant === 'sheet';
  const chrome = t.chromeBtn;
  // AAA pass 2026-08-02: chrome buttons are 44×44 hit targets (WCAG 2.5.5)
  // drawing the design's 30px circle inside.
  const chromeBtnStyle: React.CSSProperties = {
    position: 'absolute',
    top: 5,
    zIndex: 3,
    cursor: 'pointer',
    width: 44,
    height: 44,
    background: 'transparent',
    border: 0,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  const chromeCircle: React.CSSProperties = {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: chrome.bg,
    border: chrome.border,
    color: chrome.color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };
  // Glassmorphism (owner 2026-08-02): translucent fill + backdrop blur so the
  // card reads as liquid glass over whatever it covers. Alphas were picked by
  // worst-case contrast math — a white page behind Hijack's glass still yields
  // ~11:1 for body text; a black page behind TCH's yields ~14:1 (both AAA).
  // `background` (opaque) stays as the fallback where backdrop-filter is
  // unsupported, so contrast never depends on the effect landing.
  const cssVars = {
    '--d4-text': t.heading.color,
    '--d4-focus': t.input.focusBorder,
    '--d4-focus-ring': t.input.focusRing,
    '--d4-link-hover': t.linkHover,
    '--d4-method-hover-bg': t.methodHoverBg,
    '--d4-method-hover-border': t.methodHoverBorder,
    '--d4-pill-bg': t.pillBg,
    '--d4-pill-hover': t.pillHoverBg,
    '--d4-chrome-hover-border': t.methodHoverBorder
  } as React.CSSProperties;

  return (
    <div
      style={{
        ...cssVars,
        position: 'relative',
        width: sheet ? '100%' : 'min(500px, calc(100vw - 42px))',
        maxWidth: sheet ? '100%' : 420,
        // Never let a flex parent squeeze the card — without this the grid
        // stories compressed trailing cards to ~24px wide (caught by the
        // 2026-08-02 audit sweep). The card owns its own width.
        flexShrink: 0,
        boxSizing: 'border-box',
        background: t.cardBg,
        backgroundColor: t.cardBgGlass,
        backdropFilter: t.cardBackdrop,
        WebkitBackdropFilter: t.cardBackdrop,
        borderRadius: sheet ? '22px 22px 0 0' : 16,
        overflow: 'hidden',
        // The inner top highlight is modal-only bevel lighting: on the sheet's
        // tight top radius it drew a second amber line 1px inside the hairline
        // ring and read as a doubled/offset outline (owner-caught 2026-08-03).
        boxShadow: sheet ? t.cardShadow : `${t.cardShadow}, ${t.cardInnerHighlight}`
      }}>
      <style>{CARD_KEYFRAMES}</style>
      {/* Hairline drawn as an overlay ring, not a native border: with a native
          border the backdrop-filter fill clips to a slightly different curve
          and the line reads as sitting OUTSIDE the card at the sheet's top
          radius (owner-caught 2026-08-03). An inset overlay inherits the exact
          radius, so the ring always hugs the glass.

          MODAL ONLY (owner 2026-08-19). A modal is an object floating in the
          middle of the screen, and the ring is what gives it an edge. A bottom
          sheet is not floating — it is the bottom of the screen, hinged to it,
          and platform sheets accordingly have no outline: iOS's own, and every
          reference we compared against (Monzo, Airbnb, Luma, Gentler Streak,
          stoic) are an unbordered fill with a grabber. Ours had one, and it
          read as a card that had slid too far down rather than as a drawer.
          Separation on the sheet comes from the fill and `cardShadow`. */}
      {sheet ? null : (
        <div
          aria-hidden='true'
          style={{
            position: 'absolute',
            inset: 0,
            border: t.cardBorder,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            zIndex: 4
          }}
        />
      )}
      {sheet ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '9px 0 2px' }}
          aria-hidden='true'>
          <span
            style={{
              width: 38,
              height: 5,
              borderRadius: 3,
              background: skin === 'hijack' ? 'rgba(217,199,158,.4)' : 'rgba(0,0,0,.25)'
            }}
          />
        </div>
      ) : null}
      {hideChrome ? null : (
        <button
          type='button'
          aria-label='Close'
          className='d4-chrome d4-focusable'
          onClick={onClose}
          style={{ ...chromeBtnStyle, right: 3 }}>
          <span style={chromeCircle}>
            <svg width='10' height='10' viewBox='0 0 12 12' fill='none'>
              <path
                d='M2 2l8 8M10 2l-8 8'
                stroke='currentColor'
                strokeWidth='1.8'
                strokeLinecap='round'
              />
            </svg>
          </span>
        </button>
      )}
      {onBack && !hideChrome ? (
        <button
          type='button'
          aria-label='Back'
          className='d4-chrome d4-focusable'
          onClick={onBack}
          style={{ ...chromeBtnStyle, left: 3 }}>
          <span style={chromeCircle}>
            <svg width='10' height='10' viewBox='0 0 12 12' fill='none'>
              <path
                d='M8 1L3 6l5 5'
                stroke='currentColor'
                strokeWidth='1.8'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </span>
        </button>
      ) : null}
      {/* Spacing pass 2026-08-02: ≈60px to the icon disc (the design's 40px body
          pad + steps' padding-top:20px), clamped side padding for 320px screens,
          deeper bottom pad in sheet mode (design: padB 40px).

          Sheet bottom pad adds env(safe-area-inset-bottom) (owner-caught
          on-device 2026-08-20): the sheet host pulls the card DOWN past the
          inset so its background fills the home-indicator strip, which means a
          fixed 40px pad left the last line of content only ~4px above the
          indicator — inside the zone Apple reserves. Content is inset by the
          same amount the card is pulled, so its clearance no longer depends on
          which device it renders on. env() is 0 in browsers/Storybook, so the
          design's 40px is unchanged everywhere it was already correct. */}
      <div
        style={{
          padding: `${sheet ? 52 : 60}px clamp(16px, 5.5vw, 24px) ${sheet ? 40 : 22}px`,
          paddingBottom: sheet ? 'calc(40px + env(safe-area-inset-bottom, 0px))' : 22,
          boxSizing: 'border-box'
        }}>
        <div
          style={{
            animation: animate ? 'ovStepIn .3s cubic-bezier(.2,.8,.2,1)' : undefined,
            display: 'flex',
            flexDirection: 'column',
            gap: 14
          }}>
          {children}
        </div>
      </div>
    </div>
  );
}
